import type { ContextTabLocation } from '@web/hooks';

type RouteMetadata = Omit<ContextTabLocation, 'url'>;

function toLocation(url: string, metadata: RouteMetadata): ContextTabLocation {
  return { url, ...metadata };
}

function route(
  area: string,
  pageKey: string,
  name: string,
  section: string | undefined,
  iconKey = area,
): RouteMetadata {
  return { area, pageKey, name, section, iconKey };
}

export function isContextTabWorkspaceUrl(url: string, workspaceSlug: string) {
  try {
    const pathname = new URL(url, 'https://netnet.local').pathname;
    const workspaceRoot = `/${workspaceSlug}`;
    return (
      pathname === workspaceRoot || pathname.startsWith(`${workspaceRoot}/`)
    );
  } catch {
    return false;
  }
}

/** Resolves a workspace URL into stable, serializable Context Tab metadata. */
export function resolveContextTabLocation(
  url: string,
  workspaceSlug: string,
): ContextTabLocation | null {
  if (!isContextTabWorkspaceUrl(url, workspaceSlug)) return null;

  const parsed = new URL(url, 'https://netnet.local');
  const relative = parsed.pathname
    .slice(`/${workspaceSlug}`.length)
    .replace(/^\/+|\/+$/g, '');
  const parts = relative ? relative.split('/').map(decodeURIComponent) : [];

  if (parts.length === 0) {
    return toLocation(url, route('me', 'me:welcome', 'Me', 'Welcome'));
  }

  if (parts[0] === 'me') {
    const page = parts[1];
    const pages: Record<string, [string, string]> = {
      'my-tasks': ['tasks', 'My Tasks'],
      'my-lists': ['lists', 'My Lists'],
      'my-time': ['time', 'My Time'],
      'my-performance': ['performance', 'My Performance'],
    };
    const resolved = pages[page];
    if (resolved) {
      return toLocation(
        url,
        route('me', `me:${resolved[0]}`, 'Me', resolved[1]),
      );
    }
  }

  if (parts[0] === 'contacts') {
    if (parts.length === 1) {
      return toLocation(
        url,
        route('contacts', 'contacts:index', 'Contacts', undefined),
      );
    }
    if (parts[1] === 'import' && parts[2] === 'past-uploads') {
      return toLocation(
        url,
        route('contacts', 'contacts:past-uploads', 'Contacts', 'Past Uploads'),
      );
    }
    if (parts[1] === 'import') {
      return toLocation(
        url,
        route('contacts', 'contacts:import', 'Contacts', 'Import Contacts'),
      );
    }
    if (parts[1] === 'companies' && parts[2] === 'new') {
      return toLocation(
        url,
        route('contacts', 'contacts:company:new', 'Contacts', 'New Company'),
      );
    }
    if (parts[1] === 'people' && parts[2] === 'new') {
      return toLocation(
        url,
        route('contacts', 'contacts:person:new', 'Contacts', 'New Person'),
      );
    }
    if (parts[1] === 'companies' && parts[2]) {
      return toLocation(
        url,
        route(
          'contacts',
          `contacts:company:${parts[2]}`,
          'Company',
          parts[3] === 'edit' ? 'Edit Company' : 'Company',
        ),
      );
    }
    if (parts[1] === 'people' && parts[2]) {
      return toLocation(
        url,
        route(
          'contacts',
          `contacts:person:${parts[2]}`,
          'Person',
          parts[3] === 'edit' ? 'Edit Person' : 'Person',
        ),
      );
    }
  }

  if (parts[0] === 'jobs') {
    if (parts.length === 1) {
      return toLocation(url, route('jobs', 'jobs:index', 'Jobs', undefined));
    }
    if (parts[1] === 'new') {
      return toLocation(url, route('jobs', 'jobs:new', 'Jobs', 'New Job'));
    }
    const jobId = parts[1];
    const subsection = parts[2];
    const sections: Record<string, string> = {
      plan: 'Plan',
      tasks: 'Tasks',
      timeline: 'Timeline',
      'change-orders': parts[3] ? 'Change Order' : 'Change Orders',
      performance: 'Performance',
      settings: 'Settings',
    };
    return toLocation(
      url,
      route('jobs', `jobs:job:${jobId}`, 'Job', sections[subsection] ?? 'Job'),
    );
  }

  if (parts[0] === 'performance') {
    if (parts[1] === 'reports' && parts[2] === 'jobs' && parts[3]) {
      return toLocation(
        url,
        route(
          'performance',
          `performance:job:${parts[3]}`,
          'Performance • Reports',
          'Jobs',
        ),
      );
    }
    if (parts[1] === 'reports') {
      const report = parts[2] ?? 'time';
      const reports: Record<string, string> = {
        time: 'Time',
        team: 'Team',
        jobs: 'Jobs',
      };
      if (reports[report]) {
        return toLocation(
          url,
          route(
            'performance',
            `performance:${report}`,
            'Performance • Reports',
            reports[report],
          ),
        );
      }
    }
    return toLocation(
      url,
      route('performance', 'performance:index', 'Performance', undefined),
    );
  }

  if (parts[0] === 'settings') {
    const subsection = parts[1] ?? 'team';
    const sections: Record<string, string> = {
      team: 'Team',
      'service-types': 'Service Types',
      'deliverable-types': 'Deliverable Types',
      workspace: 'Workspace',
      subscription: 'Subscription',
      'terms-and-conditions': 'Terms & Conditions',
      payments: 'Payments',
    };
    return toLocation(
      url,
      route(
        'settings',
        `settings:${subsection}`,
        'Settings',
        sections[subsection] ?? 'Settings',
      ),
    );
  }

  if (parts[0] === 'quick-tasks') {
    return toLocation(
      url,
      route('quick-tasks', 'quick-tasks:index', 'Quick Tasks', undefined),
    );
  }

  if (parts[0] === 'chat') {
    const conversationId = parsed.searchParams.get('conversation_id');
    if (conversationId) {
      return toLocation(
        url,
        route(
          'chat',
          `chat:conversation:${conversationId}`,
          'Chat',
          parsed.searchParams.get('conversation_title') || 'Conversation',
        ),
      );
    }
    if (parts[1]) {
      return toLocation(
        url,
        route('chat', `chat:conversation:${parts[1]}`, 'Chat', 'Conversation'),
      );
    }
    return toLocation(url, route('chat', 'chat:index', 'Chat', undefined));
  }

  if (parts[0] === 'profile') {
    return toLocation(
      url,
      route('profile', 'profile:index', 'Profile', undefined),
    );
  }

  return null;
}
